<!-- Banner -->
<div class="rounded-2xl bg-gradient-to-r from-fuchsia-600 via-fuchsia-500 to-pink-500 text-white px-6 py-5 mb-5 shadow-lg shadow-fuchsia-200 flex flex-row items-center justify-between gap-4 flex-wrap">
  <div class="flex items-center gap-4">
    <div class="flex items-center justify-center h-12 w-12 rounded-2xl bg-white/20">
      <mat-icon class="!text-[28px] !h-7 !w-7">local_shipping</mat-icon>
    </div>
    <div>
      <div class="text-lg font-bold leading-tight">Incoming Stocks</div>
      <div class="text-sm text-fuchsia-50">Check delivered containers in as they're unloaded - their stock goes into the yard and they're marked Received.</div>
    </div>
  </div>
  <div class="flex items-center gap-1 rounded-xl bg-white/15 p-1">
    @for (y of yards; track y) {
      <button
        class="px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors"
        [ngClass]="yardFilter === y ? 'bg-white text-fuchsia-700 shadow-sm' : 'text-white hover:bg-white/20'"
        (click)="yardFilter = y"
        >
        {{ y === 'ALL' ? 'All yards' : y }} <span class="opacity-70">{{ countFor(y) }}</span>
      </button>
    }
  </div>
</div>

@if (loading) {
  <div class="flex justify-center items-center min-h-[240px]">
    <mat-spinner diameter="32"></mat-spinner>
  </div>
}

@if (!loading && !visibleReceipts.length && !visibleContainers.length) {
  <div
    class="flex flex-col items-center justify-center min-h-[240px] text-gray-400 bg-white rounded-2xl border border-gray-100 text-center px-6"
    >
    <mat-icon class="!text-4xl !h-10 !w-10 mb-2 opacity-40">inventory_2</mat-icon>
    <div>Nothing is waiting to be received{{ yardFilter === 'ALL' ? '' : ' at ' + yardFilter }}.</div>
    <div class="text-xs mt-1">Shipped goods show here once their container is set to <b class="text-gray-500">Delivered</b> on the shipment.</div>
  </div>
}

<!-- ============ Containers delivered to the yard ============ -->
@if (!loading && visibleContainers.length) {
  <div class="flex items-center gap-2 mb-3 mt-1">
    <mat-icon class="!text-[20px] !h-5 !w-5 text-fuchsia-600">view_in_ar</mat-icon>
    <div class="font-bold text-gray-900">Containers at the yard</div>
    <span class="text-xs font-semibold rounded-full px-2 py-0.5 bg-fuchsia-100 text-fuchsia-700">{{ visibleContainers.length }}</span>
    <span class="text-xs text-gray-400 ml-1 hidden sm:inline">Count what came out, then validate - quantities start at what each container should hold</span>
  </div>
  <div class="flex flex-col gap-4 mb-8">
    @for (c of visibleContainers; track c.id) {
      <app-container-receipt-card [card]="c" (received)="onContainerReceived(c)" />
    }
  </div>
}

<!-- ============ Direct deliveries (POs not on a shipment) ============ -->
@if (!loading && visibleReceipts.length) {
  <div class="flex items-center gap-2 mb-3">
    <mat-icon class="!text-[20px] !h-5 !w-5 text-fuchsia-600">local_shipping</mat-icon>
    <div class="font-bold text-gray-900">Direct deliveries</div>
    <span class="text-xs font-semibold rounded-full px-2 py-0.5 bg-fuchsia-100 text-fuchsia-700">{{ visibleReceipts.length }}</span>
    <span class="text-xs text-gray-400 ml-1 hidden sm:inline">Purchase orders that aren't on a shipment - booked in by PO</span>
  </div>
}
@if (!loading) {
  <div class="flex flex-col gap-4">
    @for (r of visibleReceipts; track r) {
      <div class="bg-white rounded-2xl border border-fuchsia-100 shadow-sm overflow-hidden">
        <!-- PO header -->
        <div class="flex flex-row items-center justify-between gap-4 flex-wrap px-5 py-4 border-b border-gray-100 bg-fuchsia-50/40">
          <div class="flex items-center gap-3 flex-wrap">
            <div class="font-bold text-gray-900 text-base">{{ r.reference }}</div>
            <span class="text-xs font-semibold px-2.5 py-1 rounded-full bg-fuchsia-600 text-white">
              {{ r.store }}
            </span>
            <span class="text-xs font-semibold px-2.5 py-1 rounded-full" [ngClass]="r.status === 'ORDERED' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'">
              {{ r.status }}
            </span>
            @if (r.supplierName) {
              <span class="text-sm text-gray-500">{{ r.supplierName }}</span>
            }
          </div>
          <div class="flex items-center gap-3">
            <span class="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full" [ngClass]="dueClass(r)">
              <mat-icon class="!text-[14px] !h-[14px] !w-[14px]">event</mat-icon>
              {{ r.expectedDeliveryDate ? (r.expectedDeliveryDate | date: 'dd MMM yyyy') : 'No expected date' }}
            </span>
            <span class="text-xs text-gray-400">{{ outstandingUnits(r) }} unit{{ outstandingUnits(r) === 1 ? '' : 's' }} outstanding</span>
          </div>
        </div>
        <!-- Lines -->
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">
              <th class="px-5 py-2.5">Product</th>
              <th class="px-3 py-2.5 text-right w-24">Ordered</th>
              <th class="px-3 py-2.5 text-right w-24">Received</th>
              <th class="px-3 py-2.5 text-right w-28">Outstanding</th>
              <th class="px-5 py-2.5 text-right w-36">Arrived now</th>
            </tr>
          </thead>
          <tbody>
            @for (item of r.items; track item) {
              <tr class="border-t border-gray-50">
                <td class="px-5 py-3">
                  <div class="font-medium text-gray-900">{{ item.productName }}</div>
                  @if (item.sku) {
                    <div class="text-xs text-gray-400 font-mono">{{ item.sku }}</div>
                  }
                </td>
                <td class="px-3 py-3 text-right text-gray-700">{{ item.quantity }}</td>
                <td class="px-3 py-3 text-right text-gray-700">{{ item.quantityReceived }}</td>
                <td class="px-3 py-3 text-right font-semibold" [ngClass]="item.outstanding ? 'text-fuchsia-700' : 'text-green-600'">
                  {{ item.outstanding }}
                </td>
                <td class="px-5 py-3 text-right">
                  <input
                    type="number"
                    min="0"
                    class="w-24 px-2.5 py-2 rounded-lg border border-gray-200 text-sm text-right font-semibold outline-none focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-100"
                    [(ngModel)]="r.receiveNow[item.id]"
                    placeholder="0"
                    />
                </td>
              </tr>
            }
          </tbody>
        </table>
        <!-- Actions -->
        <div class="flex flex-row items-center justify-between gap-3 px-5 py-3.5 border-t border-gray-100 bg-gray-50/60 flex-wrap">
          <div class="flex items-center gap-2">
            <button class="text-xs font-semibold text-fuchsia-700 hover:text-fuchsia-900 underline" (click)="receiveAll(r)">
              Receive all outstanding
            </button>
            @if (unitsToReceive(r)) {
              <button class="text-xs font-semibold text-gray-400 hover:text-gray-700 underline" (click)="clearQuantities(r)">
                Clear
              </button>
            }
          </div>
          <button
            class="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-fuchsia-600 text-white font-semibold text-sm shadow-md shadow-fuchsia-200 hover:bg-fuchsia-700 active:scale-[0.98] transition-all disabled:opacity-50"
            [disabled]="r.saving || !unitsToReceive(r)"
            (click)="bookIn(r)"
            >
            @if (r.saving) {
              <mat-spinner diameter="16"></mat-spinner>
            }
            @if (!r.saving) {
              <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">move_to_inbox</mat-icon>
            }
            {{ unitsToReceive(r) ? 'Book ' + unitsToReceive(r) + ' unit' + (unitsToReceive(r) === 1 ? '' : 's') + ' into ' + r.store + ' stock' : 'Book into ' + r.store + ' stock' }}
          </button>
        </div>
      </div>
    }
  </div>
}
